import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { ISrsFigures, ISrsPeriodFigures } from '../../../../shared/services/srs-report/srs-report.model'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * The UK SRS scope table: the current period plus whichever comparatives exist (base year, previous
 * year). Column order matches the generated document: oldest on the left, current on the right.
 * Scope 2 market-based and the market-based total are supplementary rows; totals use the
 * location-based figure.
 */
export const SrsEmissionsTable: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const periods = [figures.periods.base, figures.periods.previous, figures.periods.current]
    .filter(Boolean) as ISrsPeriodFigures[]

  if (!periods.length) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        No emissions data available for this period.
      </Typography>
    )
  }

  const row = (
    label: string,
    get: (period: ISrsPeriodFigures) => number,
    options?: { supplementary?: boolean, total?: boolean },
  ) => {
    const cellSx = {
      ...(options?.supplementary ? { color: 'text.secondary', fontStyle: 'italic' } : {}),
      ...(options?.total ? { fontFamily: 'EffraMd', borderTop: 2, borderColor: 'primary.main' } : {}),
    }
    return (
      <TableRow>
        <TableCell sx={cellSx}>{label}</TableCell>
        {periods.map((period) => (
          <TableCell align='right' key={`${label}-${period.reportPeriodId}`} sx={cellSx}>
            {fmt(get(period))}
          </TableCell>
        ))}
      </TableRow>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            {periods.map((period) => (
              <TableCell align='right' key={period.reportPeriodId}>
                {period.label} (tCO2e)
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {row('Scope 1', (period) => period.scopeOne)}
          {row('Scope 2 (location-based)', (period) => period.scopeTwoLocation)}
          {row('Scope 2 (market-based)', (period) => period.scopeTwoMarket, { supplementary: true })}
          {row('Scope 3', (period) => period.scopeThreeTotal)}
          {row('Total (Scope 1 + 2 location-based + 3)', (period) => period.totalLocation, { total: true })}
          {row('Total (Scope 1 + 2 market-based + 3)', (period) => period.totalMarket, { supplementary: true })}
        </TableBody>
      </Table>
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
        Totals use the location-based Scope 2 figure; the market-based rows are supplementary.
        Comparative columns come from your organisation's own reporting history: the previous year and
        base year are derived automatically.
      </Typography>
    </Box>
  )
}
